import React, { useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { TypedMemo } from '../../../common/types';
import { platformName } from '../platformMeta';
import { isSocialPlatform } from '../types';
import {
  failedPostOf,
  groupPostedByPeriod,
  numbersText,
  platformsOf,
  POSTS_LANE_PAGE,
  PostsBoardPostedRow,
  postedPeriodHeading,
  socialPostIdOf,
} from './postsModel';
import { postsMessages } from './postsMessages';
import { PostsLaneProps } from './postsLaneProps';
import { PostsFileRow } from './PostsFileRow';
import { PostsLane } from './PostsLane';
/* eslint-disable css-modules/no-unused-class -- a shared stylesheet; each part uses its own slice of it */
import styles from './Posts.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface PostsPostedLaneProps extends PostsLaneProps {
  rows: PostsBoardPostedRow[];
}

/**
 * Posted (design assets-rethink c1): one row per file with its published and failed posts, by
 * period, with the numbers of the daily read. A failed file says "Failed on TikTok · 8 Oct" and
 * offers Retry (the post in the Calendar). A file stays here until the user archives it (decision 3).
 */
const PostsPostedLaneComponent: React.FC<PostsPostedLaneProps> = (props) => {
  const { rows, songs, now, actions, readOnly, selectionOf, onOpen, onArchive, isHiddenOnPhone } = props;
  const intl = useIntl();
  const [isExpanded, setIsExpanded] = useState(false);
  const shown = useMemo(() => (isExpanded ? rows : rows.slice(0, POSTS_LANE_PAGE)), [isExpanded, rows]);
  const periods = useMemo(() => groupPostedByPeriod(shown, now), [now, shown]);
  const firstSocial = useMemo(() => rows.map(socialPostIdOf).find((id): id is number => id !== null) ?? null, [rows]);
  const shortDate = (iso: string): string => intl.formatDate(new Date(iso), { day: 'numeric', month: 'short' });

  return (
    <PostsLane
      title={intl.formatMessage(postsMessages.lanePosted)}
      pillClass={styles.pillPosted}
      count={rows.length}
      headAction={
        firstSocial === null ? undefined : (
          <button type="button" className={styles.link} onClick={() => actions.openSocial(firstSocial)}>
            {intl.formatMessage(postsMessages.openSocial)}
          </button>
        )
      }
      emptyText={intl.formatMessage(postsMessages.emptyPosted)}
      hiddenCount={rows.length - shown.length}
      isExpanded={isExpanded}
      onToggleMore={() => setIsExpanded((value) => !value)}
      isHiddenOnPhone={isHiddenOnPhone}
      testId="posts-lane-posted"
    >
      {periods.map((period) => (
        <React.Fragment key={period.key}>
          <div className={styles.groupHead}>{postedPeriodHeading(intl, period.key)}</div>
          {period.rows.map((row) => {
            const failed = failedPostOf(row);
            const song = row.file.catalogItemUuid === null ? undefined : songs[row.file.catalogItemUuid]?.title;
            const detail =
              failed === null
                ? intl.formatMessage(postsMessages.postedOn, {
                    song: song ?? intl.formatMessage(postsMessages.noSong),
                    date: shortDate(row.lastAt),
                  })
                : intl.formatMessage(postsMessages.failedOn, {
                    channel: isSocialPlatform(failed.platform) ? platformName(failed.platform) : failed.platform,
                    date: shortDate(failed.scheduledAt),
                  });
            const numbers = numbersText(intl, row.views, row.likes);
            return (
              <PostsFileRow
                key={row.file.key}
                file={row.file}
                detail={detail}
                platforms={platformsOf(row.posts)}
                failed={failed !== null}
                selection={selectionOf(row.file)}
                onOpen={onOpen}
                right={
                  <>
                    {failed !== null && (
                      <>
                        <span className={`${styles.pill} ${styles.pillBad}`}>
                          {intl.formatMessage(postsMessages.failed)}
                        </span>
                        {!readOnly && (
                          <button
                            type="button"
                            className={styles.link}
                            onClick={() => actions.openCalendar(failed.postUuid)}
                          >
                            {intl.formatMessage(postsMessages.retry)}
                          </button>
                        )}
                      </>
                    )}
                    {numbers !== null && <span>{numbers}</span>}
                    {!readOnly && (
                      <button
                        type="button"
                        className={`${styles.link} ${styles.hoverAction}`}
                        onClick={() => onArchive(row.file)}
                      >
                        {intl.formatMessage(postsMessages.archive)}
                      </button>
                    )}
                  </>
                }
              />
            );
          })}
        </React.Fragment>
      ))}
    </PostsLane>
  );
};

export const PostsPostedLane = TypedMemo(PostsPostedLaneComponent);
